import React, { useState, useEffect, useMemo } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination } from "swiper/modules";

// Styles
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

// Hooks & Store
import { useAppDispatch } from "../../../../../common/hooks/hooks";
import { openProductModal } from "../../../../../templates/ecommerce/product/components/common/ProductModal/productModalSlice";
import { useGetProductsByIdsQuery } from "../../../../../templates/ecommerce/product/api/productApi";
import { loadFromLocalStorage } from "../../../../../common/helpers/localStorage";
import { sortProductsByIds } from "../../../../../templates/ecommerce/wishlist/helpers/sortProductsByIds";

// Components
import ProductCard from "../../../../../templates/ecommerce/product/components/common/ProductCard/ProductCard";
import ProductCardSkeleton from "../../../../../templates/ecommerce/product/components/common/ProductCard/ProductCardSkeleton";

const STORAGE_KEY = "ecommerce_recently_viewed";
const MIN_ITEMS_TO_SHOW = 3;
const CONTAINER_ID = "recently-viewed-root";

const isNumberArray = (data: any): data is number[] => {
  return Array.isArray(data) && data.every((item) => typeof item === "number");
};

const breakpoints = {
  320: { slidesPerView: 1, spaceBetween: 20 },
  540: { slidesPerView: 2, spaceBetween: 20 },
  768: { slidesPerView: 3, spaceBetween: 30 },
  1024: { slidesPerView: 4, spaceBetween: 30 },
};

const RecentlyViewed: React.FC = () => {
  const dispatch = useAppDispatch();
  const [productIds, setProductIds] = useState<number[]>([]);
  const [isClient, setIsClient] = useState(false);
  const [config, setConfig] = useState<{ title: string; subTitle: string }>({
    title: "",
    subTitle: "",
  });

  useEffect(() => {
    setIsClient(true);
    const ids = loadFromLocalStorage<number[]>(STORAGE_KEY, isNumberArray, []);
    setProductIds(ids);

    const container = document.getElementById(CONTAINER_ID);
    if (container) {
      setConfig({
        title: container.dataset.title || "",
        subTitle: container.dataset.subTitle || "",
      });
    }
  }, []);

  const shouldSkipQuery = !isClient || productIds.length < MIN_ITEMS_TO_SHOW;

  const {
    data: products,
    isLoading,
    isError,
  } = useGetProductsByIdsQuery(productIds, {
    skip: shouldSkipQuery,
  });

  // --- SORTING LOGIC ---
  // The API returns products in arbitrary order. We must sort them
  // to match the LIFO order of `productIds` from localStorage.
  const sortedProducts = useMemo(() => {
    return sortProductsByIds(products, productIds);
  }, [products, productIds]);

  const handleOpenQuickView = (productId: number) => {
    dispatch(openProductModal({ productId }));
  };

  if (!isClient) return null;
  if (productIds.length < MIN_ITEMS_TO_SHOW) return null;

  if (isLoading) {
    return (
      <section className="recently-viewed-products">
        <div className="recently-viewed-products__container">
          <div className="recently-viewed-products__header">
            {config.subTitle && (
              <h4 className="recently-viewed-products__subtitle">
                {config.subTitle}
              </h4>
            )}
            {config.title && (
              <h2 className="recently-viewed-products__title">
                {config.title}
              </h2>
            )}
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(4, 1fr)",
              gap: "30px",
            }}
          >
            {Array.from({ length: 4 }).map((_, i) => (
              <ProductCardSkeleton key={i} />
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (isError || !sortedProducts || sortedProducts.length < MIN_ITEMS_TO_SHOW) {
    return null;
  }

  return (
    <section className="recently-viewed-products">
      <div className="recently-viewed-products__container">
        <div className="recently-viewed-products__header">
          {config.subTitle && (
            <h4 className="recently-viewed-products__subtitle">
              {config.subTitle}
            </h4>
          )}
          {config.title && (
            <h2 className="recently-viewed-products__title">{config.title}</h2>
          )}
        </div>

        <div className="recently-viewed-products__content">
          <div className="related-products">
            <Swiper
              modules={[Navigation, Pagination]}
              spaceBetween={30}
              slidesPerView={4}
              navigation={true}
              pagination={{ clickable: true }}
              className="related-products__slider"
              breakpoints={breakpoints}
            >
              {sortedProducts.map((product) => (
                <SwiperSlide key={product.id}>
                  <ProductCard
                    product={product}
                    view="grid"
                    onQuickViewClick={handleOpenQuickView}
                  />
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
        </div>
      </div>
    </section>
  );
};

export default RecentlyViewed;
